import { Injectable } from '@angular/core';
import { OnFieldChanged } from '../../../../../../shared/modules/configurable-form/models/on-field-changed';

import { UntypedFormGroup, Validators } from '@angular/forms';
import { ConfigurableFormComponent } from '../../../../../../shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { SpecFieldNames } from '../../../../specification/constants/specification-field-names.enum';
import { Store } from '@ngrx/store';
import { getTemperatureControlBusinessDaysCalendar, OrderBuilderState } from '../../../../../reducers';
import { LoadTemperatureValues } from '../../../actions/open-platform/added-values.actions';
import { getRmsRecipeSpecifications } from '../../../selectors';
import { filter, map, take, withLatestFrom, tap } from 'rxjs/operators';
import { generateAddedValueFormName } from '../../../helpers/form-added-value-name-generator';
import {
  RmsSpecificationUpdateItemAction,
  RmsSpecificationUpdatePartialItemAction,
} from '../../../actions/rms-specification.actions';
import {
  CutOffTemperatureLoadAction,
  CutOffTemperatureChangeFlagAction,
} from '../../../actions/cut-off-temperature.actions';
import { getCutOff } from '../../../selectors/cut-off.selectors';
import { combineLatest } from 'rxjs';
import { NextWorkingDayResponseV5 } from '@cemex-core/cmx-api';
import * as moment from 'moment';
import { ExperiencesService, TemperatureFeatures } from '../../../../../../shared/services/experiences.service';
import { _safe } from '../../../../../../shared/utils/safe-get.method';
import { getOpenPlatformProducts } from '../../../selectors/products.selectors';
import { FieldConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { TemperatureControlEnum } from '@ordersnproducts/modules/shared/models/temperature-control.enum';
import { BusinessDayType } from '../../../../../../../../../app/vanilla/utils/utils';
@Injectable()
export class TemperatureValidatorRequiredService implements OnFieldChanged {
  constructor() {}

  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): void {
    const summerSet = formGroup.get(SpecFieldNames.SUMMER_SET);
    if (value == undefined || value === TemperatureControlEnum.NONE) {
      summerSet.markAsPristine();
      summerSet.clearValidators();
    } else {
      summerSet.markAsPristine();
      summerSet.setValidators([Validators.required]);
    }
    summerSet.updateValueAndValidity({ emitEvent: false });

    const noneCondition = formGroup.get(SpecFieldNames.TEMPERATURE_NONE_CONDITION);
    const temperatureUnder = formGroup.get(SpecFieldNames.TEMPERATURE_UNDER_DISABLED);
    if (value === TemperatureControlEnum.NONE && !(temperatureUnder?.value ?? true)) {
      noneCondition.setValidators([Validators.requiredTrue]);
      noneCondition.markAsTouched();
      noneCondition.markAsDirty();
    } else {
      noneCondition.setValidators([]);
      noneCondition.markAsUntouched();
    }
    noneCondition.updateValueAndValidity();
  }
}

@Injectable()
export class LoadTemperatureVapsService implements OnFieldChanged {
  constructor(
    private store: Store<OrderBuilderState>,
    private experiencesService: ExperiencesService,
  ) {}

  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): void {
    if (!!formGroup) {
      const productSelected = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
      const contractSelected = formGroup.get(SpecFieldNames.CONTRACT_SELECT).value;
      const formId = formGroup.get(SpecFieldNames.ID).value;
      const temperatureEnable$ = this.experiencesService.isTemperatureEnabled$().pipe(
        take(1),
        map((isEnabled) => isEnabled !== null),
      );
      if (!!productSelected && !!contractSelected) {
        this.store
          .select(getOpenPlatformProducts)
          .pipe(
            filter((products) => !!products && products.length > 0),
            withLatestFrom(temperatureEnable$),
            filter(([products, isEnabled]) => isEnabled),
            take(1),
          )
          .subscribe(() => {
            this.store.dispatch(
              new LoadTemperatureValues({
                productCode: productSelected,
                contractCode: contractSelected,
                formId: formId,
              }),
            );
          });
      }
    }
  }
}
/**
 * This validator can only be used in the following fields
 * SpecFieldNames.TEMPERATURE_UNDER
 * SpecFieldNames.ICE_QUANTITY
 */
@Injectable()
export class ValidateCutoffTemperature implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent, config: FieldConfig): void {
    const fieldName = config.name;
    let validValue = false;
    if (fieldName === SpecFieldNames.TEMPERATURE_UNDER) {
      validValue = !!value && value !== TemperatureControlEnum.NONE;
    } else if (fieldName === SpecFieldNames.ICE_QUANTITY) {
      validValue = !!value;
    }
    if (validValue) {
      const cutoff$ = this.store.select(getCutOff);
      const temperatureCalendar$ = this.store.select(getTemperatureControlBusinessDaysCalendar);
      combineLatest([cutoff$, temperatureCalendar$])
        .pipe(
          filter(([cutoff, calendar]) => !!cutoff && !!calendar),
          take(1),
          map(([cutoff, calendar]) => {
            const businessDay = calendar.find((day) => day.type === BusinessDayType.Ok);
            if (businessDay) {
              const nextBusinessDay = businessDay.date;
              const timeZone = { ...cutoff.timeZone, IANACode: cutoff.timeZone.ianaCode };
              const nextWorkingDay: NextWorkingDayResponseV5 = {
                dateAvailable: moment(nextBusinessDay).format('YYYY-MM-DD'),
                timeCut: moment(nextBusinessDay).format('HH:mm:ss'),
                timeZone,
              };
              this.store.dispatch(new CutOffTemperatureLoadAction(nextWorkingDay, formGroup.get('id').value));
            } else {
              this.store.dispatch(new CutOffTemperatureChangeFlagAction(formGroup.get('id').value));
            }
          }),
        )
        .subscribe();
    } else {
      this.store.dispatch(new CutOffTemperatureChangeFlagAction(formGroup.get('id').value));
    }
  }
}

@Injectable()
export class ResetTemperatureVapsService implements OnFieldChanged {
  constructor(
    private store: Store<OrderBuilderState>,
    private experienceService: ExperiencesService,
  ) {}

  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): void {
    if (!!formGroup) {
      this.store
        .select(getRmsRecipeSpecifications)
        .pipe(
          take(1),
          withLatestFrom(this.experienceService.isTemperatureEnabled$()),
          filter(([forms, feature]) => !!feature),
          map(([forms, feature]) => {
            const formId = formGroup.get(SpecFieldNames.ID).value;
            const changeRecipe = forms.find((recipeForm) => recipeForm.id === formId);
            const oldValue = _safe(
              () =>
                feature === TemperatureFeatures.NITRO
                  ? changeRecipe.values.temperatureUnder
                  : changeRecipe.values.iceQuantity,
              undefined,
            );
            const fieldName =
              feature === TemperatureFeatures.NITRO ? SpecFieldNames.TEMPERATURE_UNDER : SpecFieldNames.ICE_QUANTITY;
            if (feature === TemperatureFeatures.NITRO) {
              value = value === undefined && oldValue !== undefined ? oldValue : value;
            }
            const temperature = formGroup.get(fieldName);
            if (value !== oldValue && this.validateReset(value, oldValue) && !!temperature && temperature.dirty) {
              const vapsQuantity = 4;
              for (let i = 0; i < vapsQuantity; i++) {
                const valueAddedControl = formGroup.get(generateAddedValueFormName(i));
                const controlName = generateAddedValueFormName(i);
                if (!!valueAddedControl && !!valueAddedControl.value) {
                  valueAddedControl.setValue(undefined, { emitEvent: true });
                  valueAddedControl.setErrors(null);
                  valueAddedControl.markAsPristine();
                  valueAddedControl.markAsUntouched();
                  valueAddedControl.updateValueAndValidity();

                  let valuesRaw = form.form.getRawValue();
                  valuesRaw = { ...valuesRaw, [controlName]: undefined };
                  this.store.dispatch(new RmsSpecificationUpdateItemAction({ id: form.data.id, values: valuesRaw }));
                }
              }
              if (feature === TemperatureFeatures.NITRO) {
                const temperatureCondition = formGroup.get(SpecFieldNames.TEMPERATURE_NONE_CONDITION);
                temperatureCondition.setValue(false, { emitEvent: true });
                const summerSetVap = formGroup.get(SpecFieldNames.SUMMER_SET);
                summerSetVap.setValue(undefined, { emitEvent: true });
                let values = form.form.getRawValue();
                values = { ...values, [SpecFieldNames.SUMMER_SET]: undefined };
                this.store.dispatch(new RmsSpecificationUpdateItemAction({ id: form.data.id, values }));
              }
            }
          }),
        )
        .subscribe();
    }
  }

  private validateReset(newValue, snap): boolean {
    if ((newValue === TemperatureControlEnum.NONE || !newValue) && !!snap && snap !== TemperatureControlEnum.NONE) {
      return true;
    } else if (newValue !== TemperatureControlEnum.NONE && snap === TemperatureControlEnum.NONE) {
      return true;
    } else if (newValue !== snap && !snap && newValue !== TemperatureControlEnum.NONE) {
      return true;
    }
    return false;
  }
}

@Injectable()
export class ResetTemperatureFeatureService implements OnFieldChanged {
  previousProductCode: { [formId: string]: string } = {};

  constructor(
    private store: Store<OrderBuilderState>,
    private experienceService: ExperiencesService,
  ) {}

  onFieldChanged(
    value,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: FieldConfig,
    prev: string,
  ): void {
    const productCode = _safe(() => formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value);

    if (!!formGroup) {
      this.store
        .select(getRmsRecipeSpecifications)
        .pipe(
          take(1),
          withLatestFrom(this.experienceService.isTemperatureEnabled$()),
          tap(([recipes, feature]) => {
            if (!feature) {
              return;
            }
            if (feature === TemperatureFeatures.NITRO) {
              const idFormValue = formGroup.get('id').value;
              const recipe = recipes.find((recipeForm) => recipeForm.id === idFormValue);
              if (!!recipe && (prev !== value || this.previousProductCode[idFormValue] !== productCode)) {
                const temperatureUnder = formGroup.get(SpecFieldNames.TEMPERATURE_UNDER);
                temperatureUnder.reset(undefined);
                const temperatureCondition = formGroup.get(SpecFieldNames.TEMPERATURE_NONE_CONDITION);
                temperatureCondition.setValue(false);
                const summerSetVap = formGroup.get(SpecFieldNames.SUMMER_SET);
                summerSetVap.reset(undefined);
                const values = {
                  [SpecFieldNames.TEMPERATURE_UNDER]: undefined,
                  [SpecFieldNames.SUMMER_SET]: undefined,
                };
                this.store.dispatch(new RmsSpecificationUpdatePartialItemAction({ id: idFormValue, values }));
              }
            } else if (feature === TemperatureFeatures.ICE) {
              const idFormValue = formGroup.get('id').value;
              const recipe = recipes.find((recipeForm) => recipeForm.id === idFormValue);
              if ((!!recipe && prev !== value) || this.previousProductCode[idFormValue] !== productCode) {
                const iceQuantity = formGroup.get(SpecFieldNames.ICE_QUANTITY);
                iceQuantity.setValue(undefined);
                const values: any = {
                  [SpecFieldNames.ICE_QUANTITY]: undefined,
                };
                this.store.dispatch(new RmsSpecificationUpdatePartialItemAction({ id: idFormValue, values }));
              }
            }
          }),
        )
        .subscribe();
    }
    const idFormIndex = formGroup.get('id').value;
    this.previousProductCode[idFormIndex] = productCode;
  }
}
